import React, { useMemo } from 'react';
import { DataGrid, gridClasses } from '@mui/x-data-grid';
import { useLocation, useNavigate } from 'react-router-dom';

import SmallRunStatusIndicator from './SmallRunStatusIndicator';
import SmallSequencingPlatformIndicator from './SmallSequencingPlatformIndicator.jsx';

import {
  sequencerLabelToName,
  flowcellLabelToName,
  barcodingKitLabelToName,
  libraryPrepKitLabelToName
} from '../Runs/canonicalizeMetadata.js'

const dataGridStyle = {
  [`& .${gridClasses.cell}`]: { cursor: 'pointer' },
  [`& .${gridClasses.columnHeader}, & .${gridClasses.cell}`]: {
    outline: 'transparent',
  },
  [`& .${gridClasses.columnHeader}:focus-within, & .${gridClasses.cell}:focus-within`]: {
    outline: 'none',
  },
}

function DatasetsDataGrid(props) {
  const {
    datasets,
    datasetsGridRef,
  } = props;

  const navigate = useNavigate();
  const location = useLocation();

  const dataGridColumns = [
    {
      field: 'name',
      headerName: 'Dataset Name',
      minWidth: 300,
      headerClassName: 'keynome-table--header',
      renderHeader: value => (
        <b>Dataset Name</b>
      ),
      renderCell: cell => (
        <b>{cell.value}</b>
      )
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 200,
      resizable: false,
      headerClassName: 'keynome-table--header',
      renderHeader: value => (
        <b>Status</b>
      ),
      headerAlign: 'center',
      align: 'center',
      renderCell: cell => <SmallRunStatusIndicator runId={cell.row.id} />
    },
    {
      field: 'platform',
      headerName: 'Platform',
      width: 200,
      resizable: false,
      headerClassName: 'keynome-table--header',
      renderHeader: value => (
        <b>Platform</b>
      ),
      headerAlign: 'center',
      align: 'center',
      renderCell: cell => <SmallSequencingPlatformIndicator platform={cell.value} />
    },
    {
      field: 'sequencer_name',
      headerName: 'Sequencer',
      width: 200,
      resizable: false,
      headerClassName: 'keynome-table--header',
      renderHeader: value => (
        <b>Sequencer</b>
      ),
      valueGetter: value => {
        return sequencerLabelToName[value] || value
      },
      headerAlign: 'center',
      align: 'center'
    },
    {
      field: 'flowcell',
      headerName: 'Flow Cell',
      width: 200,
      resizable: false,
      headerClassName: 'keynome-table--header',
      renderHeader: value => (
        <b>Flow Cell</b>
      ),
      valueGetter: value => {
        return flowcellLabelToName[value] || value
      },
      headerAlign: 'center',
      align: 'center'
    },
    {
      field: 'barcode_kit',
      headerName: 'Barcode/Index Kit',
      width: 200,
      resizable: false,
      headerClassName: 'keynome-table--header',
      renderHeader: value => (
        <b>Barcode / Index Kit</b>
      ),
      valueGetter: value => {
        return barcodingKitLabelToName[value] || value
      },
      headerAlign: 'center',
      align: 'center'
    },
    {
      field: 'library_prep_kit',
      headerName: 'Library Prep Kit',
      width: 200,
      resizable: false,
      headerClassName: 'keynome-table--header',
      renderHeader: value => (
        <b>Library Prep Kit</b>
      ),
      valueGetter: value => {
        return libraryPrepKitLabelToName[value] || value
      },
      headerAlign: 'center',
      align: 'center'
    },
    {
      field: 'created_at',
      headerName: 'Created At',
      width: 300,
      resizable: false,
      headerClassName: 'keynome-table--header',
      renderHeader: value => (
        <b>Created At</b>
      ),
      headerAlign: 'center',
      align: 'center',
      type: 'dateTime'
    },
  ]

  const datasetsGridRows = useMemo(() => {
    if (!datasets) return;

    return datasets
      .sort((a, b) => b.id - a.id)
      .map(run => {
        return {
          id: run.id,
          name: run.attributes.name,
          uuid: run.attributes.uuid,
          platform: run.attributes.sequencing_platform || run.attributes.platform,
          sequencer_name: run.attributes.sequencer_name,
          sequencer_sn: run.attributes.minion_sn,
          flowcell: run.attributes.flowcell_name,
          barcode_kit: run.attributes.barcoding_kit,
          library_prep_kit: run.attributes.library_prep_kit,
          created_at: new Date(run.attributes.created_at)
        }
      })
  }, [datasets]);

  return (
    <DataGrid
      autoPageSize
      columns={dataGridColumns}
      rows={datasetsGridRows}
      apiRef={datasetsGridRef}
      onRowClick={(params) => {
        const dataset = datasets.find(d => d.id === params.row.id);
        const datasetUrl = new URL(dataset.attributes.keynome_web_url);
        navigate(datasetUrl.pathname, { state: location.state });
      }}
      loading={!datasets}
      slotProps={{
        loadingOverlay: {
          variant: 'skeleton',
          noRowsVariant: 'skeleton',
        },
      }}
      sx={dataGridStyle}
    />
  );
}

export default DatasetsDataGrid;
